import { KeyboardArrowDownRounded } from "@mui/icons-material";
import { Button, Card, CardContent, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useParams } from "react-router";
import { ContainerGrid, FilterMenu, SnackbarContent } from "src/components";
import { UserMeetingsFilterByOption } from "src/constants";
import { useSession, type IUserMeetingsFilterValue } from "src/hooks";
import {
  CreateOneOnOneMeetingAsLeadModal,
  CreateOneOnOneMeetingWithLeadModal,
} from "src/pages/UserPages/UserMeetingsPage/components";
import { OneOnOneMeetingsList } from "./components";
import { useUserMeetingsSettings } from "./hooks/useUserMeetingsSettings";

interface IParams {
  userId: string;
}

interface FilterOption {
  label: string;
  value: IUserMeetingsFilterValue;
}

const filterOptions: FilterOption[] = [
  { label: "With Lead", value: UserMeetingsFilterByOption.WithLead },
  { label: "As Lead", value: UserMeetingsFilterByOption.AsLead },
];

const UserMeetingsPage = () => {
  const sessionUser = useSession();
  const { userId: userGuid } = useParams<keyof IParams>() as IParams;
  const { settings, updateSettings } = useUserMeetingsSettings(userGuid);
  const { enqueueSnackbar } = useSnackbar();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isCreateWithLeadModalVisible, setIsCreateWithLeadModalVisible] = useState(false);
  const [isCreateAsLeadModalVisible, setIsCreateAsLeadModalVisible] = useState(false);

  function handleCreateWithLeadClicked() {
    setMenuAnchor(null);
    setIsCreateWithLeadModalVisible(true);
  }

  function handleCreateAsLeadClicked() {
    setMenuAnchor(null);
    setIsCreateAsLeadModalVisible(true);
  }

  function handleMeetingCreated() {
    enqueueSnackbar(<SnackbarContent title="Meeting Created" />, {
      variant: "success",
    });
  }

  return (
    <Stack spacing={{ xs: 0.75, md: 1 }}>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Stack spacing={0.5}>
              <Typography variant="h5">Meetings</Typography>
              <FilterMenu
                options={filterOptions}
                selectedValue={settings.filterValue}
                onChange={(selectedValue) =>
                  selectedValue === undefined
                    ? updateSettings({ filterValue: undefined })
                    : updateSettings({ filterValue: selectedValue as IUserMeetingsFilterValue })
                }
              />
            </Stack>
            {sessionUser.userId === userGuid && (
              <Button
                variant="outlined"
                endIcon={<KeyboardArrowDownRounded />}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                Create 1:1 Meeting
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {sessionUser.userId === userGuid && (
        <>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem onClick={handleCreateWithLeadClicked}>With Lead</MenuItem>
            <MenuItem onClick={handleCreateAsLeadClicked}>As Lead</MenuItem>
          </Menu>

          <CreateOneOnOneMeetingWithLeadModal
            open={isCreateWithLeadModalVisible}
            onClose={() => setIsCreateWithLeadModalVisible(false)}
            onAdded={handleMeetingCreated}
          />

          <CreateOneOnOneMeetingAsLeadModal
            open={isCreateAsLeadModalVisible}
            onClose={() => setIsCreateAsLeadModalVisible(false)}
            onAdded={handleMeetingCreated}
          />
        </>
      )}

      <ContainerGrid container>
        <ContainerGrid size={{ sm: 12, md: 8 }} offset={{ md: 2 }}>
          <OneOnOneMeetingsList />
        </ContainerGrid>
      </ContainerGrid>
    </Stack>
  );
};

export { UserMeetingsPage };
